import cuid from 'cuid';

import { ApolloClient, ApolloLink, createHttpLink, InMemoryCache, split } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { getUserId, tokenUtility } from '@fluentsms/auth';
import { DEV, isUpperEnvironment, resolveEnv } from '@fluentsms/utils';
import { Client, createClient } from 'graphql-ws';

import { cacheConfiguration } from './cacheConfiguration';
import config from './config';
import { getFeatureBranchesHeader } from './devEndpointOverride';
import { cleanTypeNameLink } from './helpers/cleanTypeNameLink';
import { errorLoggingLink } from './helpers/errorLoggingLink';
import { getFullStorySessionId } from './helpers/getFullStorySessionId';
import { inflightRequestsLink } from './helpers/inflightRequestLink';
import { MessageCorrelationObject } from '@fluentsms/types-core-contracts';

const httpLink = createHttpLink({
  uri: () => config.uri,
});

const contentfulHttpLink = (spaceId: string) =>
  createHttpLink({
    uri: `https://graphql.contentful.com/content/v1/spaces/${spaceId}/`,
  });

let wsClient: Client | null = null;

export const resetWsClient = () => {
  if (wsClient) {
    wsClient.dispose();
    wsClient = null;
  }
};

const getWsClient = (): Client | null => {
  if (!wsClient && config?.uriwss) {
    try {
      wsClient = createClient({
        url: () => config.uriwss,
        retryAttempts: 3,
        lazy: true,
        connectionParams: () => ({
          authorization: `Bearer ${tokenUtility.getAccessToken()}`,
          idToken: tokenUtility.getIdToken(),
        }),
      });
    } catch (error) {
      console.error('Failed to create WebSocket client:', error);
      return null;
    }
  }
  return wsClient;
};

const featureBranchLink = setContext((_, { headers }) => {
  if (resolveEnv() !== DEV) return { headers };
  const featureHeader = getFeatureBranchesHeader();
  if (!featureHeader) return { headers };
  return {
    headers: {
      ...headers,
      'x-feature-branches': featureHeader,
    },
  };
});

const authLink = setContext((_, { headers }) => {
  const accessToken = tokenUtility.getAccessToken();
  const idToken = tokenUtility.getIdToken();

  const isAccessTokenInvalid = !tokenUtility.validateToken(accessToken);
  const isIdTokenInvalid = !tokenUtility.validateToken(idToken);

  if (isAccessTokenInvalid || isIdTokenInvalid) {
    window.location.href = '/logout';
    return { headers };
  }

  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const clientTimeOffset = new Date().getTimezoneOffset();
  const userId = getUserId();
  const pageId = typeof window !== 'undefined' ? window.location.pathname : undefined;
  const rumSessionId = getFullStorySessionId();

  const correlationObject: MessageCorrelationObject = {
    idToken: idToken ? idToken : '',
    correlationId: cuid(),
    clientTimeZone,
    clientTimeOffset,
    operationContext: {
      serviceTrace: 'Web',
      userId,
      pageId,
      rumSessionId,
    },
  };

  return {
    headers: {
      ...headers,
      authorization: accessToken ? `Bearer ${accessToken}` : '',
      'correlation-object': JSON.stringify(correlationObject),
    },
  };
});

const contentfulAuthLink = (contentDeliveryApiKey: string) => {
  return setContext((_, { headers }) => {
    return {
      headers: {
        ...headers,
        authorization: contentDeliveryApiKey ? `Bearer ${contentDeliveryApiKey}` : '',
      },
    };
  });
};

const cache = new InMemoryCache(cacheConfiguration);
const contentfulClientCache = new InMemoryCache(cacheConfiguration);

const buildWsSplitLink = (): ApolloLink => {
  const wsClientInstance = getWsClient();
  if (!wsClientInstance) {
    return httpLink;
  }

  const wsLink = new GraphQLWsLink(wsClientInstance);

  return split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition' &&
        (definition.operation === 'subscription' ||
          definition.name?.value === 'SendMessageMutation' ||
          definition.name?.value === 'AccessEntityMutation' ||
          definition.name?.value === 'CloseEntityMutation' ||
          definition.name?.value === 'GetActiveUsers')
      );
    },
    wsLink,
    httpLink,
  );
};

const mainAppLinkChain = () =>
  ApolloLink.from([
    errorLoggingLink,
    inflightRequestsLink,
    cleanTypeNameLink,
    featureBranchLink,
    authLink,
    buildWsSplitLink(),
  ]);
export const client = new ApolloClient({
  link: mainAppLinkChain(),
  cache,
  devtools: {
    enabled: !isUpperEnvironment(),
  },
});

export const setApolloLink = () => {
  client.setLink(mainAppLinkChain());
};

export const contentfulClient = (spaceId: string, contentApiKey: string) => {
  const contentfulLinkChain = ApolloLink.from([
    errorLoggingLink,
    inflightRequestsLink,
    cleanTypeNameLink,
    contentfulAuthLink(contentApiKey),
    contentfulHttpLink(spaceId),
  ]);

  return new ApolloClient({
    link: contentfulLinkChain,
    cache: contentfulClientCache,
    devtools: {
      enabled: !isUpperEnvironment(),
    },
  });
};

export default client;
